import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { PARCOURS_NODE_ORDER, PARCOURS_NODES, STAGE_LABELS, getRequiredCadenceValues } from '../data/parcoursTree'
import { isExerciseSuitableForNode, getNodeDisplayName, getNodeShortLabel, exerciseSatisfiesNodeForSingleCadence, countRelevantChords } from '../utils/nodeCriteria'
import { searchPublicExercises, getExercisesByAuthor, getExerciseDominantMode } from '../services/exerciseService'
import { HORIZONS_MUSIC_CATEGORIES, HORIZONS_STYLE_ORDER } from '../utils/tagGenerator'
import { HORIZONS_FORMATIONS } from '../data/formations'
import { getMusicalEra, getMusicalErasForCategory } from '../data/musicalEras'
import { getDisplayDifficulty } from '../utils/difficultyFromContent'
import './ParcoursCoverage.css'

/* ── Seuils effectifs Parcours — pondération 1/N (modifiables) ── */
const EFF_THRESHOLD_CRITICAL = 1     // seuils sur le score effectif (somme des 1/N)
const EFF_THRESHOLD_LOW      = 3
const EFF_THRESHOLD_OK       = 5

/* ── Seuils de couverture Horizons (modifiables, volumes différents) ── */
const H_THRESHOLD_CRITICAL = 1
const H_THRESHOLD_LOW      = 3
const H_THRESHOLD_OK       = 6

function getCoverageLevel(count, thresholds) {
  const [crit, low, ok] = thresholds
  if (count <= crit) return 'critical'
  if (count <= low)  return 'low'
  if (count <= ok)   return 'ok'
  return 'good'
}

const LEVEL_LABELS = {
  critical: 'Critique',
  low:      'Insuffisant',
  ok:       'Correct',
  good:     'Bien couvert'
}

const TYPE_LABELS = {
  learning: 'Apprentissage',
  cadence:  'Cadence',
  revision: 'Révision'
}

/* Libellés des étapes de comptage (valeurs de cadence) pour l’affichage */
const CADENCE_STEP_LABELS = {
  'demi-cadence': 'Demi-cadence',
  'perfect': 'Cadence parfaite',
  'plagal': 'Cadence plagale',
  'imperfect': 'Cadence imparfaite',
  'rompue': 'Cadence rompue'
}

const DIFFICULTIES = ['facile', 'intermédiaire', 'difficile', 'expert']

function getDecade(year) {
  if (!year || isNaN(year)) return null
  const y = Number(year)
  return `${Math.floor(y / 10) * 10}s`
}

function ParcoursCoverage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [exercises, setExercises] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('parcours')

  /* ── Charger les exercices publiés + mes brouillons ── */
  useEffect(() => {
    if (!user) return
    let cancelled = false
    async function load() {
      try {
        const [published, myExercises] = await Promise.all([
          searchPublicExercises({}),
          getExercisesByAuthor(user.uid)
        ])
        // Mes brouillons = exercices à moi qui ne sont pas publiés
        const publishedIds = new Set(published.map((ex) => ex.id))
        const myDrafts = myExercises.filter((ex) => ex.status !== 'published' && !publishedIds.has(ex.id))
        if (!cancelled) {
          setExercises([...published, ...myDrafts])
          setLoading(false)
        }
      } catch (err) {
        console.error('Erreur chargement exercices:', err)
        if (!cancelled) {
          setError('Impossible de charger les exercices.')
          setLoading(false)
        }
      }
    }
    load()
    return () => { cancelled = true }
  }, [user])

  /* ── Split publiés / brouillons ── */
  const publishedExercises = useMemo(() => exercises.filter((ex) => ex.status === 'published'), [exercises])
  const draftExercises = useMemo(() => exercises.filter((ex) => ex.status !== 'published'), [exercises])

  /* ── Map exercice → nombre de nœuds matchés (chevauchement) ── */
  const exerciseMatchCounts = useMemo(() => {
    const counts = new Map()
    for (const ex of publishedExercises) {
      const matchCount = PARCOURS_NODE_ORDER.filter(
        (nId) => isExerciseSuitableForNode(ex, nId, 2)
      ).length
      counts.set(ex.id, matchCount)
    }
    return counts
  }, [publishedExercises])

  /* ═══════════════════════════════════════════════
     PARCOURS — Calculs (étapes de comptage individuelles pour nœuds à plusieurs cadences)
     ═══════════════════════════════════════════════ */

  const coverageData = useMemo(() => {
    if (!exercises.length) return []
    const thresholds = [EFF_THRESHOLD_CRITICAL, EFF_THRESHOLD_LOW, EFF_THRESHOLD_OK]
    const rows = []
    for (const nodeId of PARCOURS_NODE_ORDER) {
      const def = PARCOURS_NODES[nodeId]
      const requiredCadences = getRequiredCadenceValues(nodeId)
      if (requiredCadences.length > 1) {
        // Nœud dual : une ligne par cadence (étapes de comptage individuelles)
        for (const cadenceValue of requiredCadences) {
          const suitablePublished = publishedExercises.filter((ex) => exerciseSatisfiesNodeForSingleCadence(ex, nodeId, cadenceValue, 2))
          const suitableDrafts = draftExercises.filter((ex) => exerciseSatisfiesNodeForSingleCadence(ex, nodeId, cadenceValue, 2))
          let major = 0, minor = 0, other = 0
          let effective = 0
          for (const ex of suitablePublished) {
            const mode = getExerciseDominantMode(ex)
            if (mode === 'major') major++
            else if (mode === 'minor') minor++
            else other++
            const matchCount = exerciseMatchCounts.get(ex.id) || 1
            effective += 1 / matchCount
          }
          const stepLabel = CADENCE_STEP_LABELS[cadenceValue] ?? cadenceValue
          const shortLabel = getNodeShortLabel(nodeId)
          rows.push({
            nodeId,
            stepKey: cadenceValue,
            stage: def?.stage ?? 0,
            type: def?.type ?? 'learning',
            label: `${shortLabel} — ${stepLabel}`,
            published: suitablePublished.length,
            drafts: suitableDrafts.length,
            major,
            minor,
            other,
            effective,
            level: getCoverageLevel(effective, thresholds)
          })
        }
      } else {
        // Nœud simple : une ligne comme avant
        const suitablePublished = publishedExercises.filter((ex) => isExerciseSuitableForNode(ex, nodeId, 2))
        const suitableDrafts = draftExercises.filter((ex) => isExerciseSuitableForNode(ex, nodeId, 2))
        let major = 0, minor = 0, other = 0
        let effective = 0
        for (const ex of suitablePublished) {
          const mode = getExerciseDominantMode(ex)
          if (mode === 'major') major++
          else if (mode === 'minor') minor++
          else other++
          const matchCount = exerciseMatchCounts.get(ex.id) || 1
          effective += 1 / matchCount
        }
        rows.push({
          nodeId,
          stepKey: null,
          stage: def?.stage ?? 0,
          type: def?.type ?? 'learning',
          label: getNodeDisplayName(nodeId),
          published: suitablePublished.length,
          drafts: suitableDrafts.length,
          major,
          minor,
          other,
          effective,
          level: getCoverageLevel(effective, thresholds)
        })
      }
    }
    return rows
  }, [exercises, publishedExercises, draftExercises, exerciseMatchCounts])

  const stages = useMemo(() => {
    const map = new Map()
    for (const item of coverageData) {
      if (!map.has(item.stage)) map.set(item.stage, [])
      map.get(item.stage).push(item)
    }
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0])
  }, [coverageData])

  const parcoursSummary = useMemo(() => {
    if (!coverageData.length) return null
    const levelOrder = { critical: 0, low: 1, ok: 2, good: 3 }
    const nodeLevels = new Map()
    const nodeEffectives = new Map()
    for (const row of coverageData) {
      const nid = row.nodeId
      const prevLevel = nodeLevels.get(nid)
      const prevEff = nodeEffectives.get(nid)
      if (prevLevel == null || levelOrder[row.level] < levelOrder[prevLevel]) {
        nodeLevels.set(nid, row.level)
      }
      const eff = prevEff != null ? Math.min(prevEff, row.effective) : row.effective
      nodeEffectives.set(nid, eff)
    }
    const uniqueNodeIds = [...new Set(coverageData.map((d) => d.nodeId))]
    const nodeCount = uniqueNodeIds.length
    const criticalNodes = uniqueNodeIds.filter((nid) => nodeLevels.get(nid) === 'critical')
    const lowNodes = uniqueNodeIds.filter((nid) => nodeLevels.get(nid) === 'low')
    const totalPub = publishedExercises.length
    const totalDraft = draftExercises.length
    const avg = totalPub / nodeCount
    const totalEff = uniqueNodeIds.reduce((acc, nid) => acc + nodeEffectives.get(nid), 0)
    const avgEff = totalEff / nodeCount
    const pubs = coverageData.map((d) => d.published)
    const min = Math.min(...pubs)
    const max = Math.max(...pubs)
    const matchValues = Array.from(exerciseMatchCounts.values())
    const avgOverlap = matchValues.length
      ? matchValues.reduce((a, b) => a + b, 0) / matchValues.length
      : 0
    return { totalPub, totalDraft, avg, avgEff, criticalNodes, lowNodes, min, max, nodeCount, avgOverlap }
  }, [coverageData, exerciseMatchCounts, publishedExercises.length, draftExercises.length])

  /* ═══════════════════════════════════════════════
     HORIZONS — Calculs
     ═══════════════════════════════════════════════ */

  const horizonsPublished = useMemo(
    () => publishedExercises.filter((ex) => ex.metadata?.section === 'horizons'),
    [publishedExercises]
  )
  const horizonsDrafts = useMemo(
    () => draftExercises.filter((ex) => ex.metadata?.section === 'horizons'),
    [draftExercises]
  )

  const horizonsCoverage = useMemo(() => {
    if (!exercises.length) return []
    const thresholds = [H_THRESHOLD_CRITICAL, H_THRESHOLD_LOW, H_THRESHOLD_OK]

    return HORIZONS_STYLE_ORDER.map((styleId) => {
      const cat = HORIZONS_MUSIC_CATEGORIES[styleId]
      const pubExercises = horizonsPublished.filter((ex) => ex.metadata?.musicCategory === styleId)
      const draftCount = horizonsDrafts.filter((ex) => ex.metadata?.musicCategory === styleId).length

      // Ventilation par difficulté (publiés seulement)
      const byDifficulty = {}
      for (const d of DIFFICULTIES) byDifficulty[d] = 0
      byDifficulty['non défini'] = 0
      for (const ex of pubExercises) {
        const diff = getDisplayDifficulty(ex.metadata?.difficulty) || 'non défini'
        if (byDifficulty[diff] !== undefined) byDifficulty[diff]++
        else byDifficulty['non défini']++
      }

      // Ventilation par formation (publiés seulement)
      const byFormation = {}
      for (const ex of pubExercises) {
        const formations = Array.isArray(ex.metadata?.formation) ? ex.metadata.formation : (ex.metadata?.formation ? [ex.metadata.formation] : [])
        for (const fId of formations) {
          byFormation[fId] = (byFormation[fId] || 0) + 1
        }
      }

      // Ventilation par décennie (publiés seulement)
      const byDecade = {}
      for (const ex of pubExercises) {
        const decade = getDecade(ex.metadata?.year)
        if (decade) byDecade[decade] = (byDecade[decade] || 0) + 1
      }

      // Ventilation par ère musicale (publiés seulement)
      const byEra = {}
      for (const ex of pubExercises) {
        const eraId = ex.metadata?.musicalEra || getMusicalEra(styleId, ex.metadata?.year)?.id
        if (eraId) byEra[eraId] = (byEra[eraId] || 0) + 1
      }

      return {
        styleId,
        label: cat?.label ?? styleId,
        tagLabel: cat?.tagLabel ?? styleId,
        published: pubExercises.length,
        drafts: draftCount,
        level: getCoverageLevel(pubExercises.length, thresholds),
        byDifficulty,
        byFormation,
        byDecade,
        byEra
      }
    })
  }, [exercises, horizonsPublished, horizonsDrafts])

  const horizonsSummary = useMemo(() => {
    if (!horizonsCoverage.length) return null
    const totalPub = horizonsPublished.length
    const totalDraft = horizonsDrafts.length
    const emptyStyles = horizonsCoverage.filter((s) => s.published === 0)
    const criticalStyles = horizonsCoverage.filter((s) => s.level === 'critical')
    return { totalPub, totalDraft, emptyStyles, criticalStyles, styleCount: HORIZONS_STYLE_ORDER.length }
  }, [horizonsCoverage, horizonsPublished, horizonsDrafts])

  /* ═══════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════ */

  if (!user) {
    return (
      <div className="parcours-coverage-page">
        <p>Chargement...</p>
      </div>
    )
  }

  return (
    <div className="parcours-coverage-page">
      {/* ── Header ── */}
      <header className="parcours-coverage-header">
        <button
          type="button"
          className="parcours-coverage-back"
          onClick={() => navigate('/dashboard')}
        >
          Retour
        </button>
        <h1>Couverture des exercices</h1>
      </header>

      <main className="parcours-coverage-main">
        {/* ── Onglets ── */}
        <div className="parcours-coverage-tabs">
          <button
            type="button"
            className={`parcours-coverage-tab ${activeTab === 'parcours' ? 'parcours-coverage-tab-active' : ''}`}
            onClick={() => setActiveTab('parcours')}
          >
            Parcours
          </button>
          <button
            type="button"
            className={`parcours-coverage-tab ${activeTab === 'horizons' ? 'parcours-coverage-tab-active' : ''}`}
            onClick={() => setActiveTab('horizons')}
          >
            Horizons
          </button>
        </div>

        {/* ── Chargement / Erreur ── */}
        {loading && (
          <div className="parcours-coverage-loading">
            <div className="spinner"></div>
            <p>Analyse des exercices en cours…</p>
          </div>
        )}

        {error && <p className="parcours-coverage-error">{error}</p>}

        {/* ═══════════════════════════════════════
            ONGLET PARCOURS
            ═══════════════════════════════════════ */}
        {!loading && !error && activeTab === 'parcours' && parcoursSummary && (
          <>
            <p className="parcours-coverage-intro">
              Nombre d'exercices applicables à chaque section du mode Parcours.
              Le statut est basé sur le <strong>score effectif</strong> (pondération 1/N :
              un exercice matchant N nœuds contribue 1/N à chaque nœud).
            </p>

            <div className="parcours-coverage-summary">
              <div className="parcours-coverage-stat">
                <span className="parcours-coverage-stat-value">{publishedExercises.length}</span>
                <span className="parcours-coverage-stat-label">Exercices publiés</span>
              </div>
              {draftExercises.length > 0 && (
                <div className="parcours-coverage-stat">
                  <span className="parcours-coverage-stat-value parcours-coverage-stat-draft">{draftExercises.length}</span>
                  <span className="parcours-coverage-stat-label">Mes brouillons</span>
                </div>
              )}
              <div className="parcours-coverage-stat">
                <span className="parcours-coverage-stat-value">{parcoursSummary.avg.toFixed(1)}</span>
                <span className="parcours-coverage-stat-label">Moyenne brute / nœud</span>
              </div>
              <div className="parcours-coverage-stat">
                <span className="parcours-coverage-stat-value">{parcoursSummary.avgEff.toFixed(1)}</span>
                <span className="parcours-coverage-stat-label">Moyenne effective / nœud</span>
              </div>
              <div className="parcours-coverage-stat">
                <span className="parcours-coverage-stat-value">{parcoursSummary.avgOverlap.toFixed(1)}</span>
                <span className="parcours-coverage-stat-label">Nœuds / exercice (moy.)</span>
              </div>
              <div className="parcours-coverage-stat">
                <span className="parcours-coverage-stat-value parcours-coverage-stat-critical">
                  {parcoursSummary.criticalNodes.length}
                </span>
                <span className="parcours-coverage-stat-label">Nœuds critiques</span>
              </div>
              <div className="parcours-coverage-stat">
                <span className="parcours-coverage-stat-value parcours-coverage-stat-low">
                  {parcoursSummary.lowNodes.length}
                </span>
                <span className="parcours-coverage-stat-label">Nœuds insuffisants</span>
              </div>
            </div>

            <div className="parcours-coverage-legend">
              <span className="parcours-coverage-legend-item parcours-coverage-level-critical">≤{EFF_THRESHOLD_CRITICAL} Critique</span>
              <span className="parcours-coverage-legend-item parcours-coverage-level-low">{EFF_THRESHOLD_CRITICAL + 1}–{EFF_THRESHOLD_LOW} Insuffisant</span>
              <span className="parcours-coverage-legend-item parcours-coverage-level-ok">{EFF_THRESHOLD_LOW + 1}–{EFF_THRESHOLD_OK} Correct</span>
              <span className="parcours-coverage-legend-item parcours-coverage-level-good">{EFF_THRESHOLD_OK + 1}+ Bien couvert</span>
              <span className="parcours-coverage-legend-hint">(score effectif)</span>
            </div>

            {stages.map(([stageNum, nodes]) => (
              <section key={stageNum} className="parcours-coverage-stage">
                <h2 className="parcours-coverage-stage-title">{STAGE_LABELS[stageNum] || `Phase ${stageNum}`}</h2>
                <div className="parcours-coverage-table">
                  <div className="parcours-coverage-table-header">
                    <span className="parcours-coverage-col-node">Nœud</span>
                    <span className="parcours-coverage-col-type">Type</span>
                    <span className="parcours-coverage-col-total">Publiés</span>
                    <span className="parcours-coverage-col-drafts">Brouil.</span>
                    <span className="parcours-coverage-col-effective">Eff.</span>
                    <span className="parcours-coverage-col-major">Maj.</span>
                    <span className="parcours-coverage-col-minor">Min.</span>
                    <span className="parcours-coverage-col-status">Statut</span>
                  </div>
                  {nodes.map((item) => (
                    <div
                      key={item.stepKey != null ? `${item.nodeId}|${item.stepKey}` : item.nodeId}
                      className={`parcours-coverage-row parcours-coverage-level-${item.level}`}
                    >
                      <span className="parcours-coverage-col-node" title={item.stepKey != null ? `${item.nodeId} — ${item.stepKey}` : item.nodeId}>
                        {item.label}
                      </span>
                      <span className="parcours-coverage-col-type">
                        {TYPE_LABELS[item.type] || item.type}
                      </span>
                      <span className="parcours-coverage-col-total">
                        <strong>{item.published}</strong>
                      </span>
                      <span className="parcours-coverage-col-drafts">
                        {item.drafts > 0 ? item.drafts : '–'}
                      </span>
                      <span className="parcours-coverage-col-effective">{item.effective.toFixed(1)}</span>
                      <span className="parcours-coverage-col-major">{item.major}</span>
                      <span className="parcours-coverage-col-minor">{item.minor}</span>
                      <span className="parcours-coverage-col-status">
                        <span className={`parcours-coverage-badge parcours-coverage-badge-${item.level}`}>
                          {LEVEL_LABELS[item.level]}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </>
        )}

        {/* ═══════════════════════════════════════
            ONGLET HORIZONS
            ═══════════════════════════════════════ */}
        {!loading && !error && activeTab === 'horizons' && horizonsSummary && (
          <>
            <p className="parcours-coverage-intro">
              Nombre d'exercices par style dans le mode Nouveaux Horizons.
              Identifiez les styles à enrichir en priorité.
            </p>

            <div className="parcours-coverage-summary">
              <div className="parcours-coverage-stat">
                <span className="parcours-coverage-stat-value">{horizonsSummary.totalPub}</span>
                <span className="parcours-coverage-stat-label">Publiés Horizons</span>
              </div>
              {horizonsSummary.totalDraft > 0 && (
                <div className="parcours-coverage-stat">
                  <span className="parcours-coverage-stat-value parcours-coverage-stat-draft">{horizonsSummary.totalDraft}</span>
                  <span className="parcours-coverage-stat-label">Mes brouillons Horizons</span>
                </div>
              )}
              <div className="parcours-coverage-stat">
                <span className="parcours-coverage-stat-value">{horizonsSummary.styleCount}</span>
                <span className="parcours-coverage-stat-label">Styles</span>
              </div>
              <div className="parcours-coverage-stat">
                <span className="parcours-coverage-stat-value parcours-coverage-stat-critical">
                  {horizonsSummary.emptyStyles.length}
                </span>
                <span className="parcours-coverage-stat-label">Styles vides</span>
              </div>
              <div className="parcours-coverage-stat">
                <span className="parcours-coverage-stat-value parcours-coverage-stat-low">
                  {horizonsSummary.criticalStyles.length}
                </span>
                <span className="parcours-coverage-stat-label">Styles critiques</span>
              </div>
            </div>

            <div className="parcours-coverage-legend">
              <span className="parcours-coverage-legend-item parcours-coverage-level-critical">0–{H_THRESHOLD_CRITICAL} Critique</span>
              <span className="parcours-coverage-legend-item parcours-coverage-level-low">{H_THRESHOLD_CRITICAL + 1}–{H_THRESHOLD_LOW} Insuffisant</span>
              <span className="parcours-coverage-legend-item parcours-coverage-level-ok">{H_THRESHOLD_LOW + 1}–{H_THRESHOLD_OK} Correct</span>
              <span className="parcours-coverage-legend-item parcours-coverage-level-good">{H_THRESHOLD_OK + 1}+ Bien couvert</span>
            </div>

            <div className="horizons-coverage-cards">
              {horizonsCoverage.map((style) => (
                <div key={style.styleId} className={`horizons-coverage-card horizons-coverage-level-${style.level}`}>
                  <div className="horizons-coverage-card-header">
                    <div className="horizons-coverage-card-title">
                      <span className="horizons-coverage-card-tag">{style.tagLabel}</span>
                      <span className="horizons-coverage-card-label">{style.label}</span>
                    </div>
                    <div className="horizons-coverage-card-count">
                      <span className="horizons-coverage-card-total">{style.published}</span>
                      {style.drafts > 0 && (
                        <span className="horizons-coverage-card-drafts" title="Mes brouillons">+{style.drafts} brouil.</span>
                      )}
                      <span className={`parcours-coverage-badge parcours-coverage-badge-${style.level}`}>
                        {LEVEL_LABELS[style.level]}
                      </span>
                    </div>
                  </div>

                  {style.published > 0 && (
                    <div className="horizons-coverage-card-details">
                      {/* Difficulté */}
                      <div className="horizons-coverage-detail-section">
                        <span className="horizons-coverage-detail-title">Difficulté</span>
                        <div className="horizons-coverage-detail-chips">
                          {DIFFICULTIES.map((d) => (
                            <span key={d} className={`horizons-coverage-chip ${style.byDifficulty[d] > 0 ? '' : 'horizons-coverage-chip-zero'}`}>
                              {d} : {style.byDifficulty[d]}
                            </span>
                          ))}
                          {style.byDifficulty['non défini'] > 0 && (
                            <span className="horizons-coverage-chip horizons-coverage-chip-zero">
                              non défini : {style.byDifficulty['non défini']}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Formations */}
                      {Object.keys(style.byFormation).length > 0 && (
                        <div className="horizons-coverage-detail-section">
                          <span className="horizons-coverage-detail-title">Formation</span>
                          <div className="horizons-coverage-detail-chips">
                            {HORIZONS_FORMATIONS.filter((f) => style.byFormation[f.id]).map((f) => (
                              <span key={f.id} className="horizons-coverage-chip">
                                {f.label} : {style.byFormation[f.id]}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Décennies */}
                      {Object.keys(style.byDecade).length > 0 && (
                        <div className="horizons-coverage-detail-section">
                          <span className="horizons-coverage-detail-title">Décennie</span>
                          <div className="horizons-coverage-detail-chips">
                            {Object.entries(style.byDecade).sort((a, b) => a[0].localeCompare(b[0])).map(([decade, count]) => (
                              <span key={decade} className="horizons-coverage-chip">
                                {decade} : {count}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Ères musicales */}
                      {Object.keys(style.byEra).length > 0 && (
                        <div className="horizons-coverage-detail-section">
                          <span className="horizons-coverage-detail-title">Ère musicale</span>
                          <div className="horizons-coverage-detail-chips">
                            {getMusicalErasForCategory(style.styleId).filter((e) => style.byEra[e.id]).map((era) => (
                              <span key={era.id} className="horizons-coverage-chip">
                                {era.label} : {style.byEra[era.id]}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  )
}

export default ParcoursCoverage
